import { ANSWER_SOURCE, useEDCFlow, validateApplicant, VALIDATION_STATUS } from "@smvtech/x-flux";
import { Button, Checkbox } from "antd";
import { useEDCFlowContext } from "pages/EDCFlow/context";
import { useMemo, useState } from "react";

const termsAndConditions = [
	<>
		I confirm that all submitted passports have <strong>more than 6 months of validity</strong> remaining from the
		date of travel.
	</>,
	<>
		Details fetched from the passport via OCR and filled in the questionnaire <strong>are accurate</strong> and{" "}
		<strong>have been verified by me.</strong>
	</>,
	<>
		The passport photograph and the applicant's submitted photograph <strong>must be of the same person.</strong>
	</>,
	<>
		To the best of my knowledge, the hotel address I entered is correct, and I understand that this address is also
		mentioned in the visa.
	</>
];

const TncSubmit = () => {
	const { activeApplicant, applicants, runApplicantValidations } = useEDCFlow();
	const { tncAccepted, setTncAccepted } = useEDCFlowContext();
	const [runningValidations, setRunningValidations] = useState(false);

	const validationResult = useMemo(() => {
		if (!activeApplicant || !applicants[activeApplicant]) {
			return {
				isComplete: false,
				questionValidations: [],
				documentValidations: [],
				applicantValidations: null
			};
		}

		return validateApplicant(
			applicants[activeApplicant],
			[ANSWER_SOURCE.PASSPORT_FRONT_OCR],
			[ANSWER_SOURCE.USER_INPUT, ANSWER_SOURCE.DOCUMENT]
		);
	}, [activeApplicant, applicants]);

	const handleSubmit = async () => {
		setRunningValidations(true);
		try {
			if (!activeApplicant) return;
			await runApplicantValidations(activeApplicant);
			setTncAccepted(true);
		} catch (error) {
			console.error(error);
		} finally {
			setRunningValidations(false);
		}
	};

	return (
		<div className="h-full w-full rounded-lg bg-white p-4 flex flex-col justify-between">
			<div className="flex flex-col gap-4">
				{termsAndConditions.map((term, index) => (
					<div key={index} className="flex items-start gap-2">
						<Checkbox checked={true} disabled />
						<span className="text-sm text-gray-800">{term}</span>
					</div>
				))}

				{tncAccepted && !validationResult.isComplete && (
					<div className="mt-2 rounded-md border border-red-600 bg-[#FEF3F2] p-3 text-xs text-red-600">
						<p className="mb-2 font-medium">Validation Errors Found, Please correct them to proceed:</p>

						<ul className="space-y-2">
							<li>
								{validationResult.applicantValidations?.status === VALIDATION_STATUS.UNFIXABLE && (
									<div className="ml-4 mt-2">
										<ul className="list-disc list-inside ml-2">
											{validationResult.applicantValidations?.unfixable.items.map((item) => (
												<li key={item.validation_name}>{item.error_text}</li>
											))}
										</ul>
									</div>
								)}
							</li>

							{validationResult.questionValidations.length > 0 && (
								<div className="ml-4 mt-2">
									<p className="font-medium">Questions</p>
									<ul className="list-disc list-inside ml-2">
										{validationResult.questionValidations.map((q) => (
											<li key={q.name}>
												{(q.validationResult?.status === VALIDATION_STATUS.UNFIXABLE ||
													q.validationResult?.status === VALIDATION_STATUS.MISSING) && (
													<>
														<span className="font-medium">{q.name}</span>
														{": "}
														<span>
															{q.validationResult.unfixable.items
																.map((item) => item.error_text)
																.join(", ")}
														</span>
													</>
												)}
											</li>
										))}
									</ul>
								</div>
							)}

							{validationResult.documentValidations.length > 0 && (
								<div className="ml-4 mt-2">
									<p className="font-medium">Documents</p>
									<ul className="list-disc list-inside ml-2">
										{validationResult.documentValidations.map((d) => (
											<li key={d.name}>
												{(d.validationResult?.status === VALIDATION_STATUS.UNFIXABLE ||
													d.validationResult?.status === VALIDATION_STATUS.MISSING) && (
													<>
														<span className="font-medium">{d.name}</span>
														{": "}
														<span>
															{d.validationResult.unfixable.items
																.map((item) => item.error_text)
																.join(", ")}
														</span>
													</>
												)}
											</li>
										))}
									</ul>
								</div>
							)}
						</ul>
					</div>
				)}

				<div className="flex items-center gap-2">
					<Button
						type="primary"
						className="!w-fit"
						onClick={handleSubmit}
						disabled={tncAccepted || runningValidations}
						loading={runningValidations}
						block>
						{tncAccepted ? "Submitted" : "Submit & Continue"}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default TncSubmit;

